import React, { useEffect, useRef, useState } from 'react';
import { crewOperation } from '../crewAccess';
import { useEquipmentControls } from '../equipmentRuntime';

type State = { revision: number; purpose: 'PREP' | 'RETURN'; confirmed: boolean; outstanding: number; warning: boolean; items: { id: string; text: string; checked: boolean }[] };
export function EquipmentTaskPanel({ taskId, status }: { taskId: string; status: string }) {
  const controls=useEquipmentControls();
  const [state, setState] = useState<State | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const pending = useRef<{ p_task: string; p_revision: number; p_item: string; p_checked: boolean; p_request_id: string } | null>(null);
  const inFlight = useRef(false);
  useEffect(() => {
    const current = ++generation.current;
    setState(null); setBusy(true); setError(''); pending.current = null;
    crewOperation('crew_task_equipment', { p_task: taskId }).then(data => {
      if (generation.current === current) setState(data);
    }).catch(e => { if (generation.current === current) setError(e.message); })
      .finally(() => { if (generation.current === current) setBusy(false); });
  const invalidatePendingRequest = () => { generation.current++; };
  return invalidatePendingRequest;
  }, [taskId, status, reload]);
  async function save() {
    if (!pending.current || inFlight.current) return;
    const current = generation.current;
    inFlight.current = true; setBusy(true); setError('');
    try {
      const data = await crewOperation('crew_task_equipment_tick', pending.current);
      if (generation.current === current) { setState(data); pending.current = null; }
    } catch (e: any) { if (generation.current === current) setError(e.message || 'Could not save. Retry or reload this task.'); }
    finally { inFlight.current = false; if (generation.current === current) setBusy(false); }
  }
  return <section aria-label="Structured task equipment" className="w-full min-w-0 space-y-3 py-3">
    <h4 className="text-base font-semibold">{state?.purpose === 'RETURN' ? 'Return & Inspect equipment' : 'Prep Gear equipment'}</h4>
    {!controls.logistics && <p role="status">Equipment task temporarily read-only. Existing progress is preserved.</p>}
    {error && <p role="alert" className="text-sm text-amber-200 break-words">{error}</p>}
    {!state && <p role="status">{busy ? 'Loading equipment…' : 'Equipment unavailable.'}</p>}
    {state?.warning && <p role="status" className="rounded-xl bg-amber-500/10 p-3 text-sm text-amber-200">Equipment changed after task completion, or items remain unchecked. Completed task status is preserved; outstanding items are not marked checked automatically.</p>}
    {state && !state.confirmed && <p>Awaiting equipment confirmation.</p>}
    {state?.confirmed && <>
      <p className="text-sm text-slate-300">{state.items.length === 0 ? 'Confirmed: no equipment required' : `${state.items.length - state.outstanding} of ${state.items.length} ${state.purpose === 'PREP' ? 'prepared' : 'returned / inspected'}`} · Revision {state.revision}</p>
      {state.items.map(item => <button key={item.id} type="button" role="checkbox" aria-checked={item.checked}
        disabled={busy || !!pending.current || !controls.logistics} onClick={() => {
          pending.current = { p_task: taskId, p_revision: state.revision, p_item: item.id, p_checked: !item.checked, p_request_id: crypto.randomUUID() }; void save();
        }} className="flex w-full min-w-0 min-h-[44px] gap-3 rounded-xl border border-cyan-500/30 bg-slate-900 p-3 text-left text-sm text-slate-100 disabled:opacity-60">
        <span aria-hidden="true">{item.checked ? '☑' : '☐'}</span><span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{item.text}</span>
      </button>)}
    </>}
    {pending.current && error && <button disabled={busy} onClick={() => void save()} className="min-h-[44px] px-3">Retry save</button>}
    <button disabled={busy} onClick={() => setReload(n => n + 1)} className="min-h-[44px] px-3 text-cyan-300">Reload equipment</button>
  </section>;
}

// Keep original line indexes for legacy instruction ticks, hiding only managed equipment.
export function managedEquipmentLine(description: string, index: number): boolean {
  const lines = description.split('\n');
  const start = lines.indexOf('[QUIKPIX EQUIPMENT BEGIN]');
  const end = lines.indexOf('[QUIKPIX EQUIPMENT END]', start + 1);
  return start >= 0 && index >= start && (end < 0 || index <= end);
}
